import { useEffect, useState } from "react";
import {
  Banner,
  BannerStatus,
  BannerVariant,
  Box,
  Button,
  ButtonVariant,
  ColorPreset,
  Field,
  Form,
  FormFieldStatus,
  H5,
  Hint,
  Input,
  Interpose,
  Label,
  Option,
  Space,
  Text,
  Glyph,
  ButtonGroup,
  ButtonLayout,
  Icon,
  PlainButton,
  HoverEffect,
  Select,
  BannerLeftAccessoryType,
  ResponsiveValue,
  ColorProp,
} from "@gocardless/flux-react";
import { CreditorBankAccountCreateRequestBody } from "@gocardless/api/dashboard/creditor-bank-account";
import { CreditorBankAccountResource } from "@gocardless/api/dashboard/types";
import { Trans } from "@lingui/macro";
import { FormProvider, Path, useForm } from "react-hook-form";
import { useLingui } from "@lingui/react";

import { MerchantOnboardingSetupEvents } from "../../common/constants/MerchantOnboardingSetupEvents";
import { countryCodesPerCurrency } from "../constants";
import { useBankDetails } from "../hooks/useBankDetails";
import { useSegmentForSetup } from "../../common/hooks/useSegmentForSetup";
import { BankDetailsDialog } from "../features/bank-details-dialog/BankDetailsDialog";
import { useSetupData } from "../../common/hooks/useSetupData";
import { useAddBankFormDescription } from "../hooks/useAddBankFormDescription";

import { AccountHolderNameField } from "./AccountHolderNameField";

import { OptimizelyFlag } from "src/technical-integrations/optimizely/constants";
import { useOptimizelyVariation } from "src/technical-integrations/optimizely/useOptimizelyVariation";
import { TrackingEvent } from "src/common/trackingEvents";
import { BankAccountFieldsByCountry } from "src/components/routes/Customers/CustomerDetail/forms/AddBankAccount/FormFields/BankAccountFieldsByCountry";
import { Currency } from "src/common/currencies";
import { cleanupBankDetails, FieldName, forCountry } from "src/config";
import {
  CountryCodes,
  CountryCodeSupported,
  countryCodeToName,
} from "src/common/country";

export enum AddBankFormVariant {
  Intro = "intro",
  Setup = "setup",
}

interface Props {
  variant: AddBankFormVariant;
  onSubmit: (bankDetails?: CreditorBankAccountResource) => void;
  currency?: Currency;
  defaultCountry?: CountryCodes;
  details?: CreditorBankAccountResource;
  onAccountDisabled?: () => void;
  isPrimaryAccount?: boolean;
  copVariantEnabled?: boolean;
  bgColor?: ResponsiveValue<ColorProp>;
  handleAmendDetails?: () => void;
}

const AddBankForm: React.FC<Props> = ({
  variant,
  defaultCountry,
  onSubmit,
  currency = Currency.Gbp,
  details = null,
  onAccountDisabled = () => {},
  isPrimaryAccount = false,
  copVariantEnabled = false,
  bgColor,
  handleAmendDetails,
}) => {
  const { sendEvent } = useSegmentForSetup();
  const { i18n } = useLingui();

  useEffect(() => {
    if (variant === AddBankFormVariant.Intro) {
      sendEvent(TrackingEvent.INTRO_BANK_ACCOUNTS_MANUALLY_ADD_PAGE_VIEWED);
    } else if (variant === AddBankFormVariant.Setup) {
      sendEvent(
        TrackingEvent.VERIFICATION_BANK_ACCOUNTS_MANUALLY_ADD_PAGE_VIEWED
      );
    }

    if (copVariantEnabled) {
      sendEvent(TrackingEvent.VERIFICATION_CONFIRMATION_OF_PAYEE_ENABLED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copVariantEnabled]);

  const defaultCountryCode =
    CountryCodeSupported[defaultCountry as CountryCodeSupported] ??
    CountryCodeSupported.GB;

  const { creditor, creditorDetails } = useSetupData();

  const { isFlagEnabled: pennyTestImprovementsEnabled } =
    useOptimizelyVariation({
      flag: OptimizelyFlag.PENNY_TEST_IMPROVEMENTS,
    });

  const [bankDetails, setBankDetails] =
    useState<CreditorBankAccountCreateRequestBody>({
      country_code: defaultCountryCode,
      account_holder_name: "",
      ...details,
    } as CreditorBankAccountCreateRequestBody);

  const [useIBAN, setUseIBAN] = useState(false);
  const [formFields, setFormFields] = useState(
    forCountry(defaultCountryCode, i18n)
  );
  const [submitting, setSubmitting] = useState(false);

  const { submit, disableAccount } = useBankDetails({
    currency,
    bankDetails,
    onSubmit,
    accountId: details?.id as string,
    onAccountDisabled: onAccountDisabled,
  });

  const countryCode = bankDetails.country_code as CountryCodeSupported;
  useEffect(() => {
    setFormFields(forCountry(countryCode, i18n));
  }, [countryCode, i18n]);

  const formMethods = useForm<CreditorBankAccountCreateRequestBody>({
    shouldUnregister: true,
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
    getValues,
  } = formMethods;

  const updateDetails = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setBankDetails({
      ...bankDetails,
      [name]: value,
    });
  };

  const closeForm = () => onSubmit();

  const getDefaultCurrency = (code: CountryCodes) =>
    BankAccountFieldsByCountry.find(
      (bankAccountFields) => bankAccountFields.code.valueOf() === code.valueOf()
    )?.default_currency;

  const showWarning = getDefaultCurrency(countryCode) !== currency;

  const addBankFormDescription = useAddBankFormDescription({
    experimentsEnabled: {
      copEnabled: copVariantEnabled,
      pennyTestImprovementsEnabled: pennyTestImprovementsEnabled,
    },
    creditorType: creditor?.creditor_type,
    companyAccountHolderName: getValues("account_holder_name"),
    creditorDetails,
  });

  return (
    <Box
      bg={bgColor || ColorPreset.BackgroundLight_03}
      borderRadius={1.5}
      borderColor={ColorPreset.BorderOnLight_03}
      borderWidth={1}
    >
      <Box gutterH={2} gutterV={2}>
        <FormProvider {...formMethods}>
          <Form
            onSubmit={handleSubmit(async (data) => {
              setSubmitting(true);

              if (variant === AddBankFormVariant.Intro) {
                sendEvent(TrackingEvent.INTRO_BANK_ACCOUNTS_STEP_COMPLETED, {
                  currency,
                });
              } else if (variant === AddBankFormVariant.Setup) {
                sendEvent(
                  currency === Currency.Gbp
                    ? MerchantOnboardingSetupEvents.BankAccountSubmitted
                    : MerchantOnboardingSetupEvents.BankAccountInternationalAccountSubmitted,
                  { page_version: "variant" }
                );
              }

              const updatedBankDetails = cleanupBankDetails(data);
              await submit?.({
                ...updatedBankDetails,
                currency,
                links: { creditor: creditor?.id ?? "" },
                set_as_default_payout_account: true,
              });
              setSubmitting(false);
            })}
          >
            <Interpose node={<Space v={2} />}>
              <Box>
                <Interpose node={<Space v={2} />}>
                  <H5 className="fs-unmask">
                    <Trans id="setup.bank-details.add-bank-form.add-bank-account-currency">
                      Add bank account ({currency})
                    </Trans>
                  </H5>
                  <Box>
                    <Text className="fs-mask">{addBankFormDescription}</Text>
                    {copVariantEnabled || pennyTestImprovementsEnabled ? (
                      <BankDetailsDialog
                        creditorType={creditor?.creditor_type}
                        sourceComponent={AddBankForm.name}
                        handleAmendDetails={handleAmendDetails}
                      />
                    ) : null}
                  </Box>
                  <AccountHolderNameField
                    updateDetails={updateDetails}
                    experimentsEnabled={{
                      copEnabled: copVariantEnabled,
                      pennyTestImprovementsEnabled,
                    }}
                    creditorType={creditor?.creditor_type}
                    creditorDetails={creditorDetails}
                    accountHolderName={bankDetails.account_holder_name}
                  />

                  <Field>
                    <Label
                      className="fs-unmask"
                      htmlFor={FieldName.countryCode}
                    >
                      <Trans id="Country">Country</Trans>
                    </Label>
                    <Select
                      id={FieldName.countryCode}
                      {...register(FieldName.countryCode, {
                        required: true,
                        value: bankDetails[FieldName.countryCode] as string,
                        onChange: updateDetails,
                      })}
                    >
                      {countryCodesPerCurrency(i18n)[currency].map((code) => {
                        const title = `${
                          countryCodeToName(i18n)[code as CountryCodes]
                        } (${code})`;

                        return (
                          <Option key={code} value={code}>
                            {title}
                          </Option>
                        );
                      })}
                    </Select>

                    {showWarning && (
                      <>
                        <Space v={0.5} />
                        <Banner
                          leftAccessory={{
                            type: BannerLeftAccessoryType.Status,
                            status: BannerStatus.Warning,
                          }}
                          variant={BannerVariant.Light}
                          title=""
                        >
                          <Trans id="setup.bank-details.add-bank-form.default_currency_banner_text">
                            The default currency for this country isn&apos;t{" "}
                            {currency}. This may result in charges by your bank
                          </Trans>
                        </Banner>
                      </>
                    )}
                  </Field>

                  {useIBAN ? (
                    <Field>
                      <Label htmlFor={FieldName.iban}>
                        {formFields.iban?.label ?? "IBAN"}
                      </Label>
                      <Input
                        id={FieldName.iban}
                        placeholder={formFields.iban?.placeholder}
                        {...register(FieldName.iban, {
                          onChange: updateDetails,
                          value: bankDetails[FieldName.iban] ?? "",
                          required: true,
                          validate: (value?: string | null) =>
                            value?.trim() && !!value?.length,
                        })}
                        className="fs-exclude"
                      />
                      {errors[FieldName.iban] && (
                        <Hint status={FormFieldStatus.Danger}>
                          <Trans id="setup.bank-details.add-bank-form.provide_iban">
                            Please provide a valid IBAN
                          </Trans>
                        </Hint>
                      )}
                    </Field>
                  ) : (
                    formFields.local?.map((field) => (
                      <Field key={field.name}>
                        <Label htmlFor={field.name}>{field.label}</Label>
                        {field.options ? (
                          <Select
                            id={field.name}
                            required
                            {...register(
                              field.name as Path<CreditorBankAccountCreateRequestBody>,
                              {
                                onChange: updateDetails,
                                required: true,
                              }
                            )}
                          >
                            {Object.keys(field.options).map((option) => (
                              <Option key={option} value={option}>
                                {option}
                              </Option>
                            ))}
                          </Select>
                        ) : (
                          <Input
                            id={field.name}
                            placeholder={field.placeholder}
                            {...register(
                              field.name as Path<CreditorBankAccountCreateRequestBody>,
                              {
                                onChange: updateDetails,
                                required: true,
                                validate: (value: string) => {
                                  if (!field.pattern) {
                                    return value.trim() && !!value.length;
                                  }

                                  return field.pattern.test(value);
                                },
                              }
                            )}
                            className="fs-exclude"
                          />
                        )}
                        {errors[
                          field.name as keyof CreditorBankAccountCreateRequestBody
                        ] && (
                          <Hint status={FormFieldStatus.Danger}>
                            <Trans id="setup.bank-details.add-bank-form.provide_valid_text">
                              Please provide a valid{" "}
                              {field.label.replace(/_/g, " ").toLowerCase()}
                            </Trans>
                          </Hint>
                        )}
                      </Field>
                    ))
                  )}
                </Interpose>
              </Box>

              {!copVariantEnabled && formFields.iban && (
                <PlainButton
                  textDecoration="underline"
                  effect={HoverEffect.TextDecoration}
                  onClick={() => setUseIBAN(!useIBAN)}
                >
                  {useIBAN ? (
                    <Text size={2}>
                      <Trans id="setup.bank-details.add-bank-form.use-local-details">
                        or click here to use local details
                      </Trans>
                    </Text>
                  ) : (
                    <Text size={2}>
                      <Trans id="setup.bank-details.add-bank-form.use-iban">
                        or click here to enter an IBAN
                      </Trans>
                    </Text>
                  )}
                </PlainButton>
              )}

              <ButtonGroup arrangement={["column-center", null, "row-start"]}>
                <Button
                  data-testid="addBankAccount"
                  type="submit"
                  layout={[ButtonLayout.Full, null, ButtonLayout.Inline]}
                  variant={ButtonVariant.PrimaryOnLight}
                  disabled={submitting}
                >
                  {details?.id ? (
                    <Text>
                      <Trans id="setup.bank-details.add-bank-form.update-bank-account">
                        Update bank account
                      </Trans>
                    </Text>
                  ) : (
                    <Trans id="setup.bank-details.add-bank-form.add-bank-account">
                      Add bank account
                    </Trans>
                  )}
                </Button>
                {!copVariantEnabled && (
                  <Button
                    layout={[ButtonLayout.Full, null, ButtonLayout.Inline]}
                    variant={ButtonVariant.TextOnLight}
                    onClick={() => closeForm()}
                  >
                    <Text>
                      <Trans>Cancel</Trans>
                    </Text>
                  </Button>
                )}
              </ButtonGroup>
              {!isPrimaryAccount && details?.id && (
                <Box>
                  <PlainButton
                    textDecoration="underline"
                    effect={HoverEffect.TextDecoration}
                    onClick={() => disableAccount({})}
                    data-testid="disable-account"
                  >
                    <Icon name={Glyph.Trash} />
                    <Space layout="inline" h={0.25} />
                    <Trans id="setup.bank-details.add-bank-form.remove-bank-account">
                      Remove bank account
                    </Trans>
                  </PlainButton>
                </Box>
              )}
            </Interpose>
          </Form>
        </FormProvider>
      </Box>
    </Box>
  );
};

export default AddBankForm;
